import React, {useEffect} from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { Container } from 'reactstrap';
import { UserLocationContext, Context } from './Store';
import Home from './views/AutoParkHome';
import Profile from './views/Profile';
import About from './views/About';
import Help from './views/Help';
import Retrieve from './views/Retrieve';

import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { orange } from '@mui/material/colors';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { ConfirmProvider } from "material-ui-confirm";
import { GeolocationProvider } from './views/Components/GeolocationContext';
import DriverSession from './auth/DriverSession';

import './App.css';
import initFontAwesome from './utils/initFontAwesome';
import Settings from './views/Settings/Settings';
import TicketDetail from './views/TicketDetail';
import TicketReceipt from './views/TicketReceipt';
import Transaction from './views/Settings/Transactions';
import Tickets from './views/Settings/Tickets';
import VehicleStatus from './views/VehicleStatus';
import BrandBar from 'components/BrandBar';
import BottomTabs from 'components/BottomTabs';
import { Notifications } from 'views/Notifications';
import SelectVehicle from 'views/SelectVehicle';
import NewVehicle from 'views/Settings/NewVehicle';
import EditVehicle from 'views/Settings/EditVehicle';
import VehiclePhotos from 'views/VehiclePhotos';
import ParkingHistory from 'views/Settings/ParkingHistory';
import Checkout from 'views/Checkout';
import SessionDetail from 'views/SessionDetail';
import NotificationSettings from 'views/Settings/NotificationSettings';
import TermsOfUse from 'views/Settings/TermsOfUse';
import PrivacyPolicy from 'views/Settings/PrivacyPolicy';
import EVCharging from 'views/Settings/EVCharging';
import EVChargingTOS from 'views/Settings/EVChargingTOS';
import EVChargingPayment from 'views/Settings/EVChargingPayment';
import EVChargingWallet from 'views/Settings/EVChargingWallet';
import EVChargingStatements from 'views/Settings/EVChargingStatements';
import NotifyAttendant from 'views/NotifyAttendant';
import SubscriptionTransactions from 'views/Settings/SubscriptionTransactions';
import ParkingHistoryForSessions from 'views/Settings/ParkingHistoryForSessions';
import UnknownUser from './views/UnknownUser';
import useDriverProfile from './hooks/useDriverProfile';
import ConfigService from "./services/ConfigService";
import types from "./Reducer/types";
import useAPI from 'useAPI';

const GENERAL_INFO_PATHS = ['/profile', '/settings', '/help', '/transactions', '/notifications-settings', '/privacy-policy', '/about', '/terms-of-use', '/ev-charging', '/ev-charging/terms', '/ev-charging/payment', '/ev-charging/wallet', '/ev-charging/statements'];

initFontAwesome();

const Main = () => {
  let navigate = useNavigate();
  const { state, dispatch } = React.useContext(Context);
  const { evChargingEnabled } = state;
  const api = useAPI();
  let location = useLocation();
  const user = useDriverProfile(api, location.pathname);

  // Load runtime feature flags on boot via the authenticated API. EV Charging
  // UI stays hidden unless the backend confirms it's enabled (and on any
  // fetch error).
  useEffect(() => {
    if (!api) return undefined;
    let active = true;
    ConfigService.init(api);
    ConfigService.getFeatures()
      .then((features) => {
        if (active) {
          dispatch({
            type: types.SET_EV_CHARGING_ENABLED,
            payload: !!features?.evChargingEnabled,
          });
        }
      })
      .catch((e) => {
        console.error('Failed to load feature flags; EV Charging stays hidden', e);
        if (active) {
          dispatch({ type: types.SET_EV_CHARGING_ENABLED, payload: false });
        }
      });
    return () => {
      active = false;
    };
  }, [api, dispatch]);

  useEffect(() => {
    const needsProfileSetup = () => {
      return user && (user.is_new || !user.is_agree || !user.phone_verified) && !GENERAL_INFO_PATHS.includes(location.pathname);
    };
    if (needsProfileSetup()) {
      try {
        navigate('/profile');
      } catch(e) {
        console.error(e);
      }
    }
  }, [user, navigate, location.pathname]);

  return (
    <div id='app' className='d-flex flex-column h-100'>
      <BrandBar />
      <Container
        className='flex-grow-1'
        style={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 80 }}
      >
        <Routes>
          <Route path='/ticket/:id' exact element={<TicketDetail />} />
          <Route path='/ticket/:id/payment/success' exact element={<TicketReceipt />} />
          <Route path='/ticket/:id/receipt' exact element={<TicketReceipt />} />
          <Route exact path='/' element={<Home />} />
          <Route exact path='/profile' element={<Profile />} />
          <Route exact path='/about' element={<About />} />
          <Route exact path='/help' element={<Help />} />
          <Route exact path='/park/vehicle/select' element={<SelectVehicle />} />
          <Route exact path='/park/vehicle/new' element={<NewVehicle />} />
          <Route exact path='/vehicles/manage' element={<SelectVehicle />} />
          <Route exact path='/vehicles/new' element={<NewVehicle />} />
          <Route exact path='/park/vehicle/:id/edit' element={<EditVehicle />} />
          <Route exact path='/vehicles/:id/edit' element={<EditVehicle />} />
          <Route exact path='/vehicles/:id/photos' element={<VehiclePhotos />} />
          <Route exact path='/retrieve' element={<Retrieve />} />
          <Route exact path='/settings' element={<Settings />} />
          <Route exact path='/notifications' element={<Notifications />} />
          <Route exact path='/transactions' element={<Transaction />} />
          <Route exact path='/subscription-transactions' element={<SubscriptionTransactions />} />
          <Route exact path='/tickets' element={<Tickets />} />
          <Route exact path='/checkout' element={<Checkout />} />
          <Route exact path='/session-detail/:id' element={<SessionDetail />} />
          <Route exact path='/vehicle-status' element={<VehicleStatus />} />
          <Route exact path='/parking-history' element={<ParkingHistory />} />
          <Route
            exact
            path='/user-parking-session-history'
            element={<ParkingHistoryForSessions />}
          />
          <Route exact path='/notify-attendant' element={<NotifyAttendant />} />
          <Route exact path='/notifications-settings' element={<NotificationSettings />} />
          <Route exact path='/terms-of-use' element={<TermsOfUse />} />
          <Route exact path='/privacy-policy' element={<PrivacyPolicy />} />
          {evChargingEnabled && (
            <>
              <Route exact path='/ev-charging' element={<EVCharging />} />
              <Route exact path='/ev-charging/terms' element={<EVChargingTOS />} />
              <Route exact path='/ev-charging/payment' element={<EVChargingPayment />} />
              <Route exact path='/ev-charging/wallet' element={<EVChargingWallet />} />
              <Route exact path='/ev-charging/statements' element={<EVChargingStatements />} />
            </>
          )}
          <Route exact path='/unknown-user' element={<UnknownUser />} />
        </Routes>
      </Container>
      <BottomTabs />
    </div>
  );
};

const ProtectedPage = () => <DriverSession><Main /></DriverSession>;

const App = () => {
  const { userLocation } = React.useContext(UserLocationContext);
  const theme = createTheme({
    palette: {
      mode: 'light',
      primary: orange,
    },
    typography: {
      button: {
        textTransform: 'none',
      },
    },
  });

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <ConfirmProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <GeolocationProvider location={userLocation}>
              <Routes>
                <Route path='*' element={<ProtectedPage />} />
              </Routes>
            </GeolocationProvider>
          </LocalizationProvider>
        </ConfirmProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
